import { Button, Container, Field, Heading, Input, Text } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"

import {
  type ApiError,
  AuthenticationService,
  type NewPassword,
} from "../client"
import useCustomToast from "../hooks/useCustomToast"
import { confirmPasswordRules, handleError, passwordRules } from "../utils"

interface NewPasswordForm extends NewPassword {
  confirm_password: string
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
  beforeLoad: async () => {
    const token = localStorage.getItem("access_token")
    if (token) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function ResetPassword() {
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<NewPasswordForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      new_password: "",
    },
  })
  const showToast = useCustomToast()
  const navigate = useNavigate()

  const resetPassword = async (data: NewPassword) => {
    const token = new URLSearchParams(window.location.search).get("token")
    if (!token) return
    await AuthenticationService.resetPassword({
      requestBody: { new_password: data.new_password, token: token },
    })
  }

  const mutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      showToast("Aktion erfolgreich!", "Passwort wurde geändert", "success")
      reset()
      navigate({ to: "/login" })
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<NewPasswordForm> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <Container
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      h="100vh"
      maxW="sm"
      alignItems="stretch"
      justifyContent="center"
      gap={4}
      centerContent
      textAlign="center"
    >
      <Heading size="4xl" color="main" mb={2}>
        Passwort zurücksetzen
      </Heading>

      <Text>Bitte gib dein neues Passwort ein.</Text>

      <Field.Root mt={4} invalid={!!errors.new_password}>
        <Field.Label htmlFor="password">Passwort</Field.Label>
        <Input
          id="password"
          {...register("new_password", passwordRules())}
          placeholder="Passwort"
          type="password"
        />
        {errors.new_password && (
          <Field.ErrorText>{errors.new_password.message}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root mt={4} invalid={!!errors.confirm_password}>
        <Field.Label htmlFor="confirm_password">
          Passwort bestätigen
        </Field.Label>
        <Input
          id="confirm_password"
          {...register("confirm_password", confirmPasswordRules(getValues))}
          placeholder="Passwort"
          type="password"
        />
        {errors.confirm_password && (
          <Field.ErrorText>{errors.confirm_password.message}</Field.ErrorText>
        )}
      </Field.Root>
      <Button variant="primary" type="submit">Passwort zurücksetzen</Button>
    </Container>
  )
}
